import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Eye, EyeOff, Loader2, CheckCircle2, AtSign, Lock, User } from "lucide-react";
import { formatApiError } from "@/lib/api";
import AuthExtras from "@/components/AuthExtras";

function PasswordField({ id, value, onChange, placeholder, testid, required, minLength }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        id={id}
        type={show ? "text" : "password"}
        required={required}
        minLength={minLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-11 rounded-xl pl-10 pr-10 text-[14px]"
        data-testid={testid}
        autoComplete={id.startsWith("l-") ? "current-password" : "new-password"}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label={show ? "Hide password" : "Show password"}
        data-testid={`${testid}-toggle`}
        tabIndex={-1}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

function TextField({ id, type = "text", value, onChange, placeholder, testid, icon: Icon, required, autoComplete }) {
  return (
    <div className="relative">
      {Icon && <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />}
      <Input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`h-11 rounded-xl ${Icon ? "pl-10" : "pl-3.5"} pr-3.5 text-[14px]`}
        data-testid={testid}
        autoComplete={autoComplete}
      />
    </div>
  );
}

function PasswordStrength({ value }) {
  const score = (() => {
    let s = 0;
    if (value.length >= 6) s++;
    if (value.length >= 10) s++;
    if (/[A-Z]/.test(value) && /[a-z]/.test(value)) s++;
    if (/\d/.test(value)) s++;
    if (/[^A-Za-z0-9]/.test(value)) s++;
    return Math.min(s, 4);
  })();
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["hsl(var(--muted))", "#EF4444", "#D98E36", "#16A34A", "#047857"];
  return (
    <div className="mt-2" aria-live="polite">
      <div className="flex gap-1.5">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-1.5 flex-1 rounded-full transition-colors"
            style={{ background: i < score ? colors[score] : "hsl(var(--muted))" }}
          />
        ))}
      </div>
      {value && (
        <div className="mt-1.5 text-[11.5px] font-semibold" style={{ color: colors[score] }}>
          {labels[score]} password
        </div>
      )}
    </div>
  );
}

export default function Auth() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("login");

  const [lEmail, setLEmail] = useState("");
  const [lPass, setLPass] = useState("");
  const [rName, setRName] = useState("");
  const [rEmail, setREmail] = useState("");
  const [rPass, setRPass] = useState("");
  const [busy, setBusy] = useState(false);

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(lEmail.trim(), lPass);
      toast.success("Welcome back!");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, "Invalid credentials"));
    } finally {
      setBusy(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(rName.trim(), rEmail.trim(), rPass);
      toast.success("Account created");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, "Sign up failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page relative min-h-screen overflow-hidden bg-background">
      {/* Ambient gradient mesh background — visible across all sizes */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 60% at 18% 22%, rgba(30,63,51,0.18) 0%, transparent 60%)," +
            "radial-gradient(50% 50% at 92% 14%, rgba(74,107,83,0.18) 0%, transparent 60%)," +
            "radial-gradient(60% 60% at 14% 88%, rgba(217,142,54,0.10) 0%, transparent 60%)," +
            "radial-gradient(40% 40% at 86% 90%, rgba(99,102,241,0.10) 0%, transparent 60%)",
        }}
      />

      <div className="relative z-10 grid min-h-screen lg:grid-cols-[1.05fr_1fr]">
        {/* Left hero panel */}
        <div className="relative hidden overflow-hidden text-primary-foreground lg:flex">
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(160deg, #1E3F33 0%, #2C5340 40%, #4A6B53 100%)",
            }}
          />
          <div
            className="absolute inset-0 opacity-25"
            style={{
              backgroundImage:
                "url(https://images.pexels.com/photos/7718459/pexels-photo-7718459.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
              backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "luminosity",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(50% 50% at 80% 10%, rgba(255,255,255,0.18) 0%, transparent 60%)," +
                "radial-gradient(50% 50% at 20% 90%, rgba(0,0,0,0.4) 0%, transparent 60%)",
            }}
          />
          <div className="relative z-10 ppage-in flex h-full w-full flex-col justify-between p-12">
            <Link to="/" className="flex items-center gap-3" data-testid="auth-brand-desktop">
              <img
                src="/icons/icon-192.png?v=2"
                alt="Expense & Save"
                width="48"
                height="48"
                className="h-12 w-12 rounded-2xl shadow-[0_8px_18px_-6px_rgba(0,0,0,0.35)]"
                loading="eager"
                decoding="async"
              />
              <div className="font-display text-[18px] font-extrabold tracking-tight">Expense&nbsp;&amp;&nbsp;Save</div>
            </Link>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] backdrop-blur-md">
                <Sparkles className="h-3.5 w-3.5" /> Built for clarity
              </div>
              <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight md:text-[54px]">
                A calmer way<br />to watch your<br />money grow.
              </h1>
              <p className="max-w-md text-[15px] leading-[1.6] text-primary-foreground/85">
                Track every dollar, set a monthly budget, and let automatic savings show you the path forward — without the spreadsheet anxiety.
              </p>
              <ul className="space-y-2.5 pt-1 text-[14px]">
                {[
                  "AI insights & spending patterns",
                  "Scan receipts & voice-log expenses",
                  "Beautiful charts, CSV & PDF exports",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2.5">
                    <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                    <span className="text-primary-foreground/90">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-[11.5px] font-medium uppercase tracking-[0.2em] text-primary-foreground/55">© {new Date().getFullYear()} Expense &amp; Save</div>
          </div>
        </div>

        {/* Right form */}
        <div className="flex items-center justify-center px-6 py-10">
          <div className="ppage-in w-full max-w-[420px]">
            {/* Glass card */}
            <div
              className="rounded-[24px] border border-white/40 bg-card/90 p-7 backdrop-blur-xl md:p-9 dark:border-white/10"
              style={{
                boxShadow:
                  "0 30px 70px -28px rgba(15,23,42,0.30), 0 6px 16px -8px rgba(15,23,42,0.08), inset 0 1px 0 rgba(255,255,255,0.5)",
              }}
              data-testid="auth-card"
            >
              <div className="mb-6 flex items-center gap-2.5 lg:hidden" data-testid="auth-brand-mobile">
                <img
                  src="/icons/icon-192.png?v=2"
                  alt="Expense & Save"
                  width="40"
                  height="40"
                  className="h-10 w-10 rounded-2xl"
                  loading="eager"
                  decoding="async"
                />
                <div className="font-display text-[17px] font-extrabold tracking-tight">Expense & Save</div>
              </div>

              <h2 className="font-display text-[28px] font-extrabold tracking-tight md:text-[30px]">
                {tab === "login" ? "Welcome back" : "Create your account"}
              </h2>
              <p className="mt-1.5 text-[13.5px] text-muted-foreground">
                {tab === "login"
                  ? "Sign in to continue managing your money."
                  : "Start tracking your money in under a minute."}
              </p>

              <Tabs value={tab} onValueChange={setTab} className="mt-6">
                <TabsList className="grid w-full grid-cols-2 rounded-full bg-muted/70 p-1">
                  <TabsTrigger value="login" className="rounded-full text-[13px] font-bold data-[state=active]:bg-card data-[state=active]:shadow-sm" data-testid="auth-tab-login">Sign in</TabsTrigger>
                  <TabsTrigger value="register" className="rounded-full text-[13px] font-bold data-[state=active]:bg-card data-[state=active]:shadow-sm" data-testid="auth-tab-register">Sign up</TabsTrigger>
                </TabsList>

                <TabsContent value="login" className="mt-6">
                  <form onSubmit={submitLogin} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="l-email" className="text-[12.5px] font-semibold">Email</Label>
                      <TextField
                        id="l-email" type="email" value={lEmail} onChange={setLEmail}
                        placeholder="roshankumar@gmail.com" testid="login-email-input"
                        icon={AtSign} required autoComplete="email"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="l-pass" className="text-[12.5px] font-semibold">Password</Label>
                      <PasswordField id="l-pass" value={lPass} onChange={setLPass} placeholder="Min. 6 characters" testid="login-password-input" required minLength={6} />
                    </div>
                    <Button
                      type="submit" disabled={busy}
                      className="group h-11 w-full rounded-full bg-primary text-[14px] font-bold text-primary-foreground shadow-[0_14px_28px_-10px_hsl(var(--primary)/0.55)] hover:bg-primary/90 hover:shadow-[0_18px_32px_-10px_hsl(var(--primary)/0.65)]"
                      data-testid="login-submit-button"
                    >
                      {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</>) : (<>Sign in <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>)}
                    </Button>
                  </form>
                  <AuthExtras />
                </TabsContent>

                <TabsContent value="register" className="mt-6">
                  <form onSubmit={submitRegister} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="r-name" className="text-[12.5px] font-semibold">Name</Label>
                      <TextField id="r-name" value={rName} onChange={setRName} placeholder="Roshan Kumar" testid="register-name-input" icon={User} required autoComplete="name" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="r-email" className="text-[12.5px] font-semibold">Email</Label>
                      <TextField id="r-email" type="email" value={rEmail} onChange={setREmail} placeholder="roshankumar@gmail.com" testid="register-email-input" icon={AtSign} required autoComplete="email" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="r-pass" className="text-[12.5px] font-semibold">Password</Label>
                      <PasswordField id="r-pass" value={rPass} onChange={setRPass} placeholder="Min. 6 characters" testid="register-password-input" required minLength={6} />
                      <PasswordStrength value={rPass} />
                    </div>
                    <Button
                      type="submit" disabled={busy}
                      className="group h-11 w-full rounded-full bg-primary text-[14px] font-bold text-primary-foreground shadow-[0_14px_28px_-10px_hsl(var(--primary)/0.55)] hover:bg-primary/90 hover:shadow-[0_18px_32px_-10px_hsl(var(--primary)/0.65)]"
                      data-testid="register-submit-button"
                    >
                      {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Creating account…</>) : (<>Create account <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>)}
                    </Button>
                  </form>
                  <AuthExtras />
                </TabsContent>
              </Tabs>

              <p className="mt-6 text-center text-[11.5px] text-muted-foreground">
                By continuing you agree to keep your finances tidy. 🌱
              </p>
            </div>
            {/* Footer note */}
            <p className="mt-5 text-center text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground/70">
              Bank-grade encryption · Your data, your control
            </p>
          </div>
        </div>
      </div>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}
